import { computed, defineComponent, onMounted, ref, watch } from '@vue/composition-api';
import WidgetMenu from 'components/DataWidgets/WidgetMenu/WidgetMenu.vue';
import WidgetFrame from 'src/components/widgets/WidgetFrame/WidgetFrame.vue';
import icon from 'assets/img/dashboards/dash-satisfaction.svg';
import errorIcon from 'assets/icons/alert-bubble.svg';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import Timeline from 'src/components/project/analysis/results/widgets/Timeline.vue';
import { getCsvData as CSVData, regroupData as regroup } from './NPSTimeline.utils';
import { getAggregationOffset, getDataAbsMax, makeDoubleTimelineSlide } from '../DataWidgetUtils';
import WidgetMessagePanel from 'components/widgets/WidgetMessagePanel/WidgetMessagePanel.vue';
export default defineComponent({
    components: {
        WidgetFrame,
        DownloadExportButton,
        Timeline,
        WidgetMenu,
        WidgetMessagePanel,
    },
    props: {
        /** data to render */
        data: { type: Object, required: false, default: null },
        overallData: { type: Object, required: false, default: null },
        exportName: { type: String, required: false, default: '' },
        /** `fetching`, `done` or `''` */
        status: { type: String, required: false, default: '' },
        /** error object for dev panel */
        error: { type: [Error, Object], required: false, default: null },
        /** nicer error message for user  */
        userError: { type: String, required: false, default: null },
        startTime: { type: Number, required: false, default: null },
        doneTime: { type: Number, required: false, default: null },
        devMode: { type: Boolean, required: false, default: false },
        /** widget banner to display */
        banner: { type: Object, default: () => null, required: false },
        dateFields: { type: Array, required: true },
        defaultDateField: { type: String, required: false, default: null },
        weekStart: { type: String, required: false, default: null },
        group: { type: String, required: false, default: 'overall__' },
        /** Add a skeleton mask (used when reloading state between dashboards) */
        masked: { type: Boolean, required: false, default: false },
        config: { type: Object, required: false, default: null },
        dayFirstDates: { type: Boolean, required: false, default: false },
    },
    setup(props, { emit }) {
        const root = ref(null);
        const selectedDataInPlottableFormat = ref([]);
        const records = ref({});
        const yRangeLeft = ref([0, 1]);
        const yRangeRight = ref([0, 1]);
        const menuSelections = ref({
            'Date Field': '',
            'Resolution': 'Monthly',
        });
        const displayOptions = ref({
            seriesLabels: {
                'impact': 'Impact on NPS',
                'nps': 'NPS',
                'promoters': 'Promoters %',
                'passives': 'Passives %',
                'detractors': 'Detractors %',
            },
            numberType: 'signAwareInteger',
            yAxisLabelLeft: 'NPS',
            yCapValue: 200,
            yMultipleOf: 5,
        });
        // If the timeline is being shown on the dashboard overview, then the data
        // is the overall data. This means we cannot calculate impact on X because
        // there is no subset of data to use in the calculation. (e.g if we were
        // showing the stats for a theme, the impact would be calculated using
        // the impact of the theme ON the overall dataset).
        const rightYAxisOptions = computed(() => {
            if (hasImpact.value) {
                return {
                    label: 'Impact on NPS',
                    color: '#f89516',
                    names: ['impact'],
                };
            }
            return {
                label: 'NPS Category (%)',
                color: '#7f7f7f',
                names: ['promoters', 'passives', 'detractors'],
            };
        });
        const hasImpact = computed(() => {
            return props.group !== 'overall__';
        });
        const overallStats = computed(() => {
            const data = props.overallData?.data?.payload
                .find(({ group__ }) => group__ === props.group);
            if (data && hasImpact.value) {
                return [
                    {
                        label: 'Impact on NPS',
                        class: 'impact',
                        value: +data['NPS Category|npsi_rto__'] ?? 0,
                        percent: false,
                    }, {
                        label: 'NPS',
                        class: 'nps',
                        value: +data['NPS Category|nps__'] ?? 0,
                        percent: false,
                    },
                ];
            }
            if (data && !hasImpact.value) {
                return [
                    {
                        label: 'NPS',
                        class: 'nps',
                        value: +data['NPS Category|nps__'] ?? 0,
                        percent: false,
                    }, {
                        label: 'Promoters',
                        class: 'promoters',
                        value: +data['NPS Category|Promoter%__'] ?? 0,
                        percent: true,
                    }, {
                        label: 'Detractors',
                        class: 'detractors',
                        value: +data['NPS Category|Detractor%__'] ?? 0,
                        percent: true,
                    }, {
                        label: 'Passives',
                        class: 'passives',
                        value: +data['NPS Category|Passive%__'] ?? 0,
                        percent: true,
                    },
                ];
            }
            return [];
        });
        const menus = computed(() => {
            return [{
                    name: 'Date Field',
                    selection: menuSelections.value['Date Field'],
                    options: [
                        [{
                                title: 'Date Field',
                                type: 'menu',
                                showSelected: true,
                                selected: menuSelections.value['Date Field'],
                                options: props.dateFields.map(({ name }) => name)
                            }]
                    ],
                }, {
                    name: 'Resolution',
                    selection: menuSelections.value['Resolution'],
                    options: [
                        [{
                                title: 'Resolution',
                                type: 'menu',
                                showSelected: true,
                                selected: menuSelections.value['Resolution'],
                                options: ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly']
                            }]
                    ],
                }];
        });
        const hasDate = computed(() => {
            return props.dateFields.length > 0;
        });
        const getPrefix = (n, percent) => {
            if (percent)
                return '';
            if (n > 0)
                return '+';
            return '';
        };
        const getSuffix = (percent) => {
            if (percent)
                return '%';
            return '';
        };
        const fetchData = (force = false) => {
            const blocks = [{
                    'aggfuncs': [{
                            'new_column': 'frequency',
                            'src_column': 'document_id',
                            'aggfunc': 'count'
                        }, {
                            'new_column': 'frequency',
                            'src_column': 'document_id',
                            'aggfunc': 'count'
                        }],
                    "pivot_field": "NPS Category",
                    "metric_calculator": "nps"
                }];
            const requirements = {
                blocks,
                date_fieldname: menuSelections.value['Date Field'],
                date_aggregation_offset: getAggregationOffset(menuSelections.value['Resolution']),
                week_start: props.weekStart,
            };
            if (hasDate.value) {
                // Data by date resolution
                emit('requires', 'nps-timeline', requirements, force, true);
            }
            // Overall data
            emit('requires', 'nps-timeline-overall', {
                blocks: [{
                        "aggfuncs": [{
                                "new_column": "frequency",
                                "src_column": "document_id",
                                "aggfunc": "count"
                            }],
                        "pivot_field": "NPS Category",
                        "metric_calculator": "nps"
                    }]
            }, force, true);
        };
        const refresh = () => {
            window.location.reload();
        };
        const contact = () => {
            try {
                window.Intercom('show');
            }
            catch {
                console.warn('intercom show failed');
            }
        };
        const reload = () => {
            fetchData(true);
        };
        const updateConfig = () => {
            const options = {
                dateField: menuSelections.value["Date Field"],
                resolution: menuSelections.value["Resolution"],
            };
            const updated = Object.assign({}, props.config, { options });
            emit('config-changed', updated);
        };
        const setMenuSelection = (_, path, fromConfig = false) => {
            menuSelections.value[path[0]] = path[1];
            if (!fromConfig)
                updateConfig();
        };
        const setOptionsFromConfig = () => {
            setMenuSelection(null, ['Resolution', props.config?.options?.resolution ?? 'Monthly'], true);
            if (hasDate.value) {
                setMenuSelection(null, ['Date Field', props.config?.options?.dateField ?? (props.defaultDateField ?? props.dateFields[0].name)], true);
            }
            else {
                setMenuSelection(null, ['Date Field', ''], true);
            }
        };
        const calculateYRange = () => {
            const visibleDataset = selectedDataInPlottableFormat.value.filter(series => series.visible);
            if (visibleDataset.length === 0)
                return;
            const dataMaxNPS = getDataAbsMax([visibleDataset.find(({ name }) => name === 'nps')], displayOptions.value.yCapValue, displayOptions.value.yMultipleOf);
            yRangeLeft.value = [-dataMaxNPS, dataMaxNPS];
            if (hasImpact.value) {
                const dataMaxImpact = getDataAbsMax([visibleDataset.find(({ name }) => name === 'impact')], displayOptions.value.yCapValue, displayOptions.value.yMultipleOf);
                yRangeRight.value = [-dataMaxImpact, dataMaxImpact];
            }
            else {
                // NPS Categories are percentages
                yRangeRight.value = [0, 100];
            }
        };
        const regroupData = () => {
            if (!hasDate.value)
                return;
            const [recs, groupedData] = regroup(props.data, menuSelections.value['Date Field'], props.group, hasImpact.value ? ['nps', 'impact'] : ['nps', 'promoters', 'passives', 'detractors']);
            // only show series with data
            selectedDataInPlottableFormat.value = groupedData.filter(({ counts = [] }) => counts.length > 0);
            records.value = recs;
            calculateYRange();
        };
        const getCsvData = () => {
            return CSVData(selectedDataInPlottableFormat.value, menuSelections.value);
        };
        const getTrendEl = () => {
            return root.value?.$el.querySelector('div.content');
        };
        const getExportConfig = () => {
            return {
                dims: getTrendEl()?.getBoundingClientRect(),
                css: `
          text {
            color: #383838;
            font-size: 14px;
            stroke: none;
          }
          .line {
            stroke-width: 2px;
          }
          .axis path, .axis line {
            shape-rendering: crispEdges;
            stroke: #ebebeb;
            stroke-width: 2px;
            opacity: 0.5;
          }
        `
            };
        };
        const makePptSlide = (pptx) => {
            const slide = pptx.addSlide();
            makeDoubleTimelineSlide(pptx, slide, [
                selectedDataInPlottableFormat.value.slice(0, 1),
                selectedDataInPlottableFormat.value.slice(1),
            ], props.exportName + ' - NPS Timeline', ['NPS', 'NPS Category (%)'], props.dayFirstDates);
        };
        watch(menuSelections, () => {
            fetchData();
        }, {
            deep: true,
        });
        watch(() => props.data, (val) => {
            if (val === null)
                return;
            regroupData();
        });
        watch(() => props.config, () => {
            setOptionsFromConfig();
        }, {
            deep: true,
        });
        watch(() => props.group, () => {
            fetchData();
        });
        onMounted(() => {
            setOptionsFromConfig();
            if (props.data === null || props.overallData === null) {
                // regroupData and set OverallData will get called by the watchers
                // when the data is available
                fetchData();
            }
            else {
                // the data we need is available so we can immediately call the
                // wrangler functions
                regroupData();
            }
        });
        return {
            icon,
            errorIcon,
            refresh,
            contact,
            reload,
            getPrefix,
            getSuffix,
            overallStats,
            displayOptions,
            rightYAxisOptions,
            menuSelections,
            menus,
            root,
            getCsvData,
            getExportConfig,
            getTrendEl,
            hasDate,
            setMenuSelection,
            selectedDataInPlottableFormat,
            yRangeLeft,
            yRangeRight,
            records,
            calculateYRange,
            makePptSlide,
        };
    },
});
